import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormFieldDto } from "../../Models/FormFieldDto";
import FormApi from "../../Api/FormApi";
import {
  BoothAvailability,
  getNeighborBoothKeys,
  parseBoothRegion,
  parseBoothNumeric,
  sortBoothNames,
} from "../../Models/BoothRegion";

interface BoothPickerFieldProps {
  formId: number;
  field: FormFieldDto;
  value: string;
  onChange: (boothNumber: string) => void;
  required?: boolean;
}

const POLL_MS = 45000;

const BoothPickerField: React.FC<BoothPickerFieldProps> = ({
  formId,
  field,
  value,
  onChange,
}) => {
  const [availability, setAvailability] = useState<BoothAvailability[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAvailability = useCallback(async () => {
    if (!field.fieldId) return;
    try {
      const res = await FormApi.getBoothAvailability(formId, field.fieldId);
      setAvailability(
        res.data.map((a) => ({
          boothNumber: a.boothNumber,
          companyName: a.companyName,
          submittedDate: a.submittedDate,
          responseId: a.responseId,
        }))
      );
    } catch (err) {
      console.error("Failed to load booth availability", err);
    } finally {
      setLoading(false);
    }
  }, [formId, field.fieldId]);

  useEffect(() => {
    loadAvailability();
    const id = setInterval(loadAvailability, POLL_MS);
    return () => clearInterval(id);
  }, [loadAvailability]);

  const assigneeByBooth = useMemo(() => {
    const map = new Map<string, string>();
    availability.forEach((a) => {
      map.set(a.boothNumber.trim().toLowerCase(), a.companyName);
    });
    return map;
  }, [availability]);

  const reservedSet = useMemo(
    () => new Set(assigneeByBooth.keys()),
    [assigneeByBooth]
  );

  const options = field.options || [];
  const allBoothNames = useMemo(
    () => options.map((o) => o.name).filter(Boolean),
    [options]
  );

  const neighborKeys = useMemo(() => {
    if (!value.trim()) return new Set<string>();
    return getNeighborBoothKeys(value, allBoothNames);
  }, [value, allBoothNames]);

  const sortedBoothRoster = useMemo(() => {
    return sortBoothNames(allBoothNames).map((name) => {
      const key = name.trim().toLowerCase();
      const assignee = assigneeByBooth.get(key) ?? "Unassigned";
      const isYours =
        value.trim().toLowerCase() === key && !assigneeByBooth.has(key);
      return {
        name,
        assignee: isYours ? "Your selection (pending)" : assignee,
        reserved: assigneeByBooth.has(key),
      };
    });
  }, [allBoothNames, assigneeByBooth, value]);

  const getAssigneeLabel = (boothName: string, isSelected: boolean): string => {
    const key = boothName.trim().toLowerCase();
    if (isSelected && !assigneeByBooth.has(key)) return "Your selection";
    return assigneeByBooth.get(key) ?? "Unassigned";
  };

  const handleBoothClick = (boothName: string) => {
    if (reservedSet.has(boothName.trim().toLowerCase())) return;
    onChange(boothName);
  };

  if (!field.defaultValue) {
    return (
      <p className="text-amber-700 text-sm">
        Booth map is not configured yet.
      </p>
    );
  }

  const reservedCount = availability.length;
  const totalBooths = allBoothNames.length;

  return (
    <div className="space-y-4">
      {!value && (
        <p className="text-base font-semibold text-gray-800">
          Click an available booth on the map below to make your selection.
        </p>
      )}

      <div className="relative inline-block max-w-full border rounded overflow-hidden bg-white">
        <img
          src={field.defaultValue}
          alt={field.label || "Floorplan"}
          className="block max-w-full h-auto"
        />
        {options.map((opt, idx) => {
          const region = parseBoothRegion(opt.description);
          if (!region || !opt.name) return null;
          const [left, top, width, height] = region.coords;
          const key = opt.name.trim().toLowerCase();
          const reserved = reservedSet.has(key);
          const selected = value.trim().toLowerCase() === key;
          const assigneeLabel = getAssigneeLabel(opt.name, selected);

          let className =
            "absolute box-border border-2 transition-colors flex items-center justify-center text-xs font-semibold ";
          if (reserved) {
            className +=
              "border-gray-500 bg-gray-500/50 cursor-not-allowed text-white ";
          } else if (selected) {
            className +=
              "border-blue-700 bg-blue-600/50 cursor-pointer text-white ring-2 ring-blue-400 ";
          } else {
            className +=
              "border-green-600 bg-green-500/30 hover:bg-green-500/50 cursor-pointer text-gray-900 ";
          }

          return (
            <button
              key={`${opt.name}-${idx}`}
              type="button"
              disabled={reserved}
              title={`Booth ${opt.name} — ${assigneeLabel}`}
              className={className}
              style={{
                left: `${left}%`,
                top: `${top}%`,
                width: `${width}%`,
                height: `${height}%`,
              }}
              onClick={() => handleBoothClick(opt.name)}
            >
              {opt.name}
            </button>
          );
        })}
      </div>

      {value ? (
        <div
          className="rounded-lg border-2 border-blue-700 bg-blue-50 px-5 py-4 shadow-sm"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm font-bold uppercase tracking-wide text-blue-900 mb-1">
            Your booth selection
          </p>
          <p className="text-3xl font-bold text-blue-950 leading-tight">
            Booth {value}
          </p>
          {parseBoothNumeric(value) !== null && neighborKeys.size > 1 && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-sm font-bold text-blue-900 mb-2">
                Nearby booths
              </p>
              <ul className="space-y-1 text-sm">
                {sortBoothNames(
                  allBoothNames.filter((n) =>
                    neighborKeys.has(n.trim().toLowerCase())
                  )
                ).map((name) => {
                  const isSel = name.trim().toLowerCase() === value.trim().toLowerCase();
                  return (
                    <li key={name} className={isSel ? "font-bold text-blue-950" : "text-blue-900"}>
                      Booth {name}: {getAssigneeLabel(name, isSel)}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <p className="text-sm text-blue-800 mt-2">
            This is the booth that will be submitted with your registration.
          </p>
          <button
            type="button"
            className="mt-3 text-sm font-semibold text-blue-700 underline hover:text-blue-900"
            onClick={() => onChange("")}
          >
            Change selection
          </button>
        </div>
      ) : null}

      <div className="text-sm">
        <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 border-2 border-green-600 bg-green-500/30" />
            Available
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 border-2 border-gray-500 bg-gray-500/50" />
            Reserved
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-4 h-4 border-2 border-blue-700 bg-blue-600/50" />
            Your selection
          </span>
          {loading && <span>Updating availability…</span>}
        </div>

        <details className="border border-gray-300 rounded-lg bg-white shadow-sm group">
          <summary className="cursor-pointer list-none px-4 py-3 font-bold text-gray-900 hover:bg-gray-50 rounded-lg [&::-webkit-details-marker]:hidden">
            <span className="flex items-center justify-between gap-2">
              <span>Selected Booths</span>
              <span className="text-sm font-semibold text-gray-600">
                {reservedCount} reserved / {totalBooths} total
                <span className="ml-2 text-gray-400 group-open:rotate-180 inline-block transition-transform">
                  ▼
                </span>
              </span>
            </span>
          </summary>
          <div className="border-t border-gray-200 max-h-80 overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-100 sticky top-0">
                <tr>
                  <th className="p-2 border-b font-bold">Booth</th>
                  <th className="p-2 border-b font-bold">Assigned to</th>
                </tr>
              </thead>
              <tbody>
                {sortedBoothRoster.map((row) => (
                  <tr
                    key={row.name}
                    className={
                      row.name.trim().toLowerCase() === value.trim().toLowerCase()
                        ? "bg-blue-50"
                        : row.reserved
                          ? "bg-gray-50"
                          : ""
                    }
                  >
                    <td className="p-2 border-b font-semibold">{row.name}</td>
                    <td
                      className={`p-2 border-b ${
                        row.assignee === "Unassigned"
                          ? "text-gray-500 italic"
                          : "font-medium text-gray-900"
                      }`}
                    >
                      {row.assignee}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalBooths === 0 && (
              <p className="p-4 text-gray-500 text-sm">No booths configured on this form.</p>
            )}
          </div>
        </details>
      </div>
    </div>
  );
};

export default BoothPickerField;
